import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Info, Sliders } from 'lucide-react';
import { calculateNeededToPass, calculateWeightedTotals, clampPercentage } from '../utils/whatIfCalculator';

const formatPercent = (value) => `${value.toFixed(2)}%`;

const WhatIfCalculator = ({ gradeBook, classList, sharedRubric }) => {
    const students = useMemo(() => classList?.students || [], [classList?.students]);
    const projects = useMemo(() => gradeBook?.projects || [], [gradeBook?.projects]);
    const grades = gradeBook?.grades || {};

    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [whatIfValues, setWhatIfValues] = useState({});

    const defaultPassingThreshold = useMemo(() => {
        const rubricThreshold = sharedRubric?.assignmentInfo?.passingThreshold;
        const gradeBookThreshold = gradeBook?.metadata?.passingThreshold;
        const candidate = Number.isFinite(rubricThreshold)
            ? rubricThreshold
            : Number.isFinite(gradeBookThreshold)
                ? gradeBookThreshold
                : 60;

        return clampPercentage(candidate);
    }, [gradeBook?.metadata?.passingThreshold, sharedRubric?.assignmentInfo?.passingThreshold]);

    const [passingThreshold, setPassingThreshold] = useState(defaultPassingThreshold);

    useEffect(() => {
        setPassingThreshold(defaultPassingThreshold);
    }, [defaultPassingThreshold]);

    useEffect(() => {
        if (!students.length) {
            setSelectedStudentId('');
            return;
        }

        setSelectedStudentId((prev) => {
            if (prev && students.some((student) => student.id === prev)) {
                return prev;
            }
            return students[0]?.id || '';
        });
    }, [students]);

    const updateWhatIfValue = (projectId, value) => {
        if (!selectedStudentId) return;

        setWhatIfValues((prev) => {
            const studentValues = prev[selectedStudentId] || {};
            return {
                ...prev,
                [selectedStudentId]: {
                    ...studentValues,
                    [projectId]: clampPercentage(value)
                }
            };
        });
    };

    const handleSetAll = (value) => {
        if (!selectedStudentId) return;

        const normalizedValue = clampPercentage(value);
        const studentValues = projects.reduce((acc, project) => {
            const grade = grades[selectedStudentId]?.[project.id];
            const isEditable = !Number.isFinite(grade?.percentage) || grade?.percentage === 0;
            if (isEditable) {
                acc[project.id] = normalizedValue;
            }
            return acc;
        }, {});

        setWhatIfValues((prev) => ({
            ...prev,
            [selectedStudentId]: {
                ...(prev[selectedStudentId] || {}),
                ...studentValues
            }
        }));
    };

    const studentValues = useMemo(
        () => whatIfValues[selectedStudentId] || {},
        [whatIfValues, selectedStudentId]
    );
    const studentGrades = useMemo(
        () => grades[selectedStudentId] || {},
        [grades, selectedStudentId]
    );

    const rows = useMemo(() => {
        return projects.map((project) => {
            const grade = studentGrades[project.id];
            const percent = Number.isFinite(grade?.percentage) ? grade.percentage : null;
            const isEditable = percent === null || percent === 0;
            const whatIfValue = studentValues[project.id] ?? 0;
            const appliedPercent = isEditable ? whatIfValue : percent ?? 0;
            const weightAchieved = (project.weight * appliedPercent) / 100;

            return {
                id: project.id,
                name: project.name,
                weight: project.weight,
                percent,
                isEditable,
                whatIfValue,
                appliedPercent,
                weightAchieved
            };
        });
    }, [projects, studentGrades, studentValues]);

    const completedItems = rows.filter((row) => !row.isEditable && Number.isFinite(row.percent));
    const remainingItems = rows.filter((row) => row.isEditable);

    const completedTotals = calculateWeightedTotals(
        completedItems.map((row) => ({ weight: row.weight, percent: row.percent }))
    );

    const totalTotals = calculateWeightedTotals(
        rows.map((row) => ({ weight: row.weight, percent: row.appliedPercent }))
    );

    const totalWeight = totalTotals.totalWeight;
    const completedWeight = completedItems.reduce((sum, row) => sum + row.weight, 0);
    const remainingWeight = remainingItems.reduce((sum, row) => sum + row.weight, 0);
    const currentEarnedGrade = totalWeight > 0
        ? (completedTotals.earnedContribution / totalWeight) * 100
        : 0;
    const currentAverageGrade = completedWeight > 0
        ? (completedTotals.earnedContribution / completedWeight) * 100
        : 0;
    const maximumAchievable = totalWeight > 0
        ? ((completedTotals.earnedContribution + remainingWeight) / totalWeight) * 100
        : 0;

    const neededToPass = calculateNeededToPass({
        targetPercent: passingThreshold,
        earnedContribution: completedTotals.earnedContribution,
        remainingWeight,
        totalWeight
    });

    const requiredAverage = neededToPass.requiredPercent;
    const requiredAverageClamped = requiredAverage == null
        ? null
        : clampPercentage(requiredAverage);

    const completedAverage = completedItems.length
        ? completedItems.reduce((sum, row) => sum + (row.percent || 0), 0) / completedItems.length
        : null;

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <Calculator className="text-blue-600" />
                            What-If Calculator
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                            Explore how ungraded items could impact final grades using weighted averages.
                        </p>
                    </div>
                    <div className="text-center lg:text-right">
                        <div className="text-sm text-gray-500">What-If Calculated Grade</div>
                        <div className="text-3xl font-bold text-blue-700">
                            {formatPercent(totalTotals.percentage || 0)}
                        </div>
                    </div>
                </div>

                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                            <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                                {classList?.courseMetadata?.courseCode
                                    ? `${classList.courseMetadata.courseCode} - ${classList.courseMetadata.courseName || 'Course'}`
                                    : gradeBook?.metadata?.courseCode
                                        ? `${gradeBook.metadata.courseCode} - ${gradeBook.metadata.courseName || 'Course'}`
                                        : 'No class loaded'}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                            <select
                                value={selectedStudentId}
                                onChange={(event) => setSelectedStudentId(event.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                {!students.length && (
                                    <option value="">No students available</option>
                                )}
                                {students.map((student) => (
                                    <option key={student.id} value={student.id}>
                                        {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex gap-2">
                            <Info size={16} className="mt-0.5" />
                            <span>
                                What-if values apply only to missing or zero scores. Completed items remain read-only.
                            </span>
                        </div>
                    </div>

                    <div className="bg-gray-50 border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-3">Grade Details</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Current Grade (Earned)</span>
                                <span className="font-medium">
                                    {formatPercent(Number.isFinite(currentEarnedGrade) ? currentEarnedGrade : 0)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Current Grade (Average)</span>
                                <span className="font-medium">
                                    {completedWeight > 0
                                        ? formatPercent(Number.isFinite(currentAverageGrade) ? currentAverageGrade : 0)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Maximum Achievable Grade</span>
                                <span className="font-medium">
                                    {formatPercent(Number.isFinite(maximumAchievable) ? maximumAchievable : 0)}
                                </span>
                            </div>
                        </div>

                        <div className="mt-4 border-t pt-4">
                            <h5 className="font-semibold text-gray-800 mb-2">Needed to Pass</h5>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={passingThreshold}
                                    onChange={(event) => setPassingThreshold(clampPercentage(event.target.value))}
                                    className="w-20 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-600">% threshold</span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700">
                                {remainingWeight <= 0 ? (
                                    <span>No remaining items to improve the grade.</span>
                                ) : requiredAverage != null && requiredAverage <= 0 ? (
                                    <span>Already at or above the target grade.</span>
                                ) : requiredAverage != null && requiredAverage > 100 ? (
                                    <span className="text-red-600">
                                        Target is not possible with remaining items.
                                    </span>
                                ) : requiredAverageClamped != null ? (
                                    <span>
                                        To reach {passingThreshold}%, you need {requiredAverageClamped.toFixed(1)}% on remaining items (average).
                                    </span>
                                ) : (
                                    <span>Set a passing threshold to see requirements.</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg border space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Set all what-if values to:</span>
                    <button
                        onClick={() => handleSetAll(0)}
                        className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                        0%
                    </button>
                    {completedAverage != null && (
                        <button
                            onClick={() => handleSetAll(completedAverage)}
                            className="px-3 py-1.5 text-sm rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700"
                        >
                            Average %
                        </button>
                    )}
                    <button
                        onClick={() => handleSetAll(100)}
                        className="px-3 py-1.5 text-sm rounded-lg bg-green-100 hover:bg-green-200 text-green-700"
                    >
                        100%
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="text-left border-b">
                            <tr>
                                <th className="py-2 px-2">Grade Item</th>
                                <th className="py-2 px-2">Score Achieved</th>
                                <th className="py-2 px-2">Weight Achieved</th>
                                <th className="py-2 px-2">What-If</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id} className="border-b last:border-b-0">
                                    <td className="py-2 px-2 font-medium text-gray-800">
                                        {row.name}
                                        <div className="text-xs text-gray-500">Weight: {row.weight}%</div>
                                    </td>
                                    <td className="py-2 px-2">
                                        {row.percent != null
                                            ? `${row.percent}%`
                                            : 'Not graded'}
                                    </td>
                                    <td className="py-2 px-2">
                                        {row.weightAchieved.toFixed(2)} / {row.weight.toFixed(2)}
                                    </td>
                                    <td className="py-2 px-2">
                                        {row.isEditable ? (
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={row.whatIfValue}
                                                    onChange={(event) => updateWhatIfValue(row.id, event.target.value)}
                                                    className="w-32"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <Sliders size={16} className="text-gray-400" />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={row.whatIfValue}
                                                        onChange={(event) => updateWhatIfValue(row.id, event.target.value)}
                                                        className="w-20 p-1 border border-gray-300 rounded"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-500">Completed</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={4} className="py-6 text-center text-gray-500">
                                        Load a gradebook with projects to start the what-if calculator.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default WhatIfCalculator;
